import type { StreamedMessage } from "@unbox/api";
import { useCallback } from "react";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import { useSSE } from "@/providers/sse-provider";

/**
 * Listens for `resource_refreshed` SSE events and invalidates affected queries.
 * The actual data re-fetch happens through the normal Tanstack Query path —
 * this provider is just the cache-invalidation seam.
 *
 * Add new resource types to INVALIDATE_BY_TYPE as the refresh pattern expands.
 */
const INVALIDATE_BY_TYPE: Record<string, string[]> = {
  collab: [
    "target-collaboration-detail",
    "target-collaboration-creators",
    "target-collaborations",
    "target-collaborations-list",
    "video-collaborations",
  ],
};

export const ResourceRefreshProvider = ({ children }: { children: React.ReactNode }) => {
  const handleSSE = useCallback((message: StreamedMessage) => {
    if (message.event !== "resource_refreshed") return;

    const keys = INVALIDATE_BY_TYPE[message.type];
    if (!keys) return;

    for (const key of keys) {
      queryClient.invalidateQueries({ queryKey: [key], exact: false });
    }
  }, []);

  useSSE(handleSSE);

  return <>{children}</>;
};
